/**
 * THE TWO FIREBASE PROJECTS, AND WHICH ONE THIS BUILD AUTHENTICATES AGAINST.
 *
 * One project used to authenticate BOTH the dev environments and every
 * production tenant, so a dev account was a real credential in the same
 * identity pool as paying customers' users. The projects are split; this file
 * is how a build picks its side.
 *
 * THE VALUES ARE NOT SECRET AND BELONG IN THE REPO. A Firebase web API key is
 * public by design — it ships in the bundle, anyone can read it, and security
 * comes from Firebase Auth rules plus the authorised-domain list, not from
 * keeping the key hidden. Holding both configs here rather than in twenty-four
 * `.env` files means the two projects sit side by side where a wrong value is
 * visible in a diff, instead of scattered where a single mistyped `projectId`
 * is invisible until a tenant cannot log in.
 *
 * ONLY THE MODE COMES FROM THE ENVIRONMENT, because only the mode varies per
 * tenant build. Each tenant is a dev-tier or a prod-tier tenant, CI already
 * builds once per tenant, and `.env.<tenant>` already carries every other
 * per-tenant value — so this rides rails that exist rather than laying new ones.
 *
 * AUTHENTICATION IS NOT AFFECTED BY HOSTING. Every tenant site is still served
 * by `salesguru-86316`; that project keeps its Hosting job and loses only its
 * monopoly on identity. Nothing here touches DNS, certificates or `.firebaserc`.
 */

export interface FirebaseProjectConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export type FirebaseMode = 'prod' | 'dev';

export const FIREBASE_PROJECTS: Record<FirebaseMode, FirebaseProjectConfig> = {
  /** Every production tenant. Also still the Hosting project for ALL tenants. */
  prod: {
    apiKey: 'AIzaSyAilL9nJ8HxbsARFnwLQkRcN-rb0g-H5wg',
    authDomain: 'salesguru-86316.firebaseapp.com',
    projectId: 'salesguru-86316',
    storageBucket: 'salesguru-86316.firebasestorage.app',
    messagingSenderId: '808622255984',
    appId: '1:808622255984:web:76809a019e7852dc6d29b5',
  },
  /** The dev-tier tenants: dev, demo, apexia — and local development. */
  dev: {
    apiKey: 'AIzaSyBrZvPmC0VNBYN7akSoVtse-eZaHg-s5A4',
    authDomain: 'salesguru-dev.firebaseapp.com',
    projectId: 'salesguru-dev',
    storageBucket: 'salesguru-dev.firebasestorage.app',
    messagingSenderId: '71064575436',
    appId: '1:71064575436:web:1172bf58e037b67eaa5169',
  },
};

/**
 * The mode this build was compiled for.
 *
 * THERE IS NO DEFAULT, DELIBERATELY. A missing or unrecognised value throws at
 * startup rather than falling back, because both possible fallbacks are bad in
 * a way nobody would notice: defaulting to `prod` silently puts dev users in
 * the production identity pool — the exact defect this split exists to remove —
 * and defaulting to `dev` would point a paying customer's site at an internal
 * project. A build that cannot say which side it is on must not start.
 */
export function resolveFirebaseMode(raw: string | undefined): FirebaseMode {
  if (raw === 'prod' || raw === 'dev') {
    return raw;
  }
  throw new Error(
    `REACT_APP_FIREBASE_MODE must be "prod" or "dev" (got ${raw === undefined ? 'undefined' : `"${raw}"`}). ` +
      'Set it in this tenant\'s .env file — dev, demo and apexia are "dev"; every other tenant is "prod".',
  );
}

export function firebaseConfigForMode(mode: FirebaseMode): FirebaseProjectConfig {
  return FIREBASE_PROJECTS[mode];
}
